import type { AddDockerRegistryRequest, DockerRegistry } from "@microtica/ms-ap-sdk";

import { apiClient, getBaseUrl, getIdToken } from ".";

/**
 * How Microtica signs in to an ECR registry: with a stored IAM access key, or keyless through the
 * project's connected AWS account role. Registries saved before this existed have no `auth`, and
 * use access keys. Other registry types have no `auth`.
 */
export type RegistryAuth = "access_key" | "connected_account";

// `auth` and the switch route aren't in @microtica/ms-ap-sdk yet
export type DockerRegistryWithAuth = DockerRegistry & { auth?: RegistryAuth };
export type AddDockerRegistryWithAuthRequest = AddDockerRegistryRequest & { auth?: RegistryAuth };

export const REGISTRY_AUTH_LABEL: Record<RegistryAuth, string> = {
    access_key: "Access keys",
    connected_account: "Connected AWS account",
};

/** The registry's auth mode; only ECR registries have one */
export function registryAuth(registry: { type: string; auth?: string }): RegistryAuth | undefined {
    if (registry.type !== "ecr") return undefined;
    return registry.auth === "connected_account" ? "connected_account" : "access_key";
}

/**
 * Moves an access-key ECR registry to the connected AWS account. The API checks the role can use
 * ECR first. The old key stays stored and the IAM user isn't touched.
 */
export async function switchRegistryToConnectedAccount(projectId: string, registryName: string) {
    const { data } = await apiClient.put(
        `${getBaseUrl()}/ms-ap/docker-registries/${encodeURIComponent(registryName)}/auth`,
        { auth: "connected_account" },
        { headers: { Authorization: `Bearer ${getIdToken()}`, projectId } },
    );
    return data as { done: boolean };
}
